import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Plug, Monitor, Eye, Edit, Trash2, Tv } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useWatch } from '../../contexts/WatchContext'
import AddScheduleModal from './AddScheduleModal'
import ModifyScheduleModal from './ModifyScheduleModal'
import { useWatchedDeviceService } from '../../hooks/useWatchedDeviceService'
import { useWatchedScreenService } from '../../hooks/useWatchedScreenService'
import { createPortal } from 'react-dom'
import { canAddSchedules } from '@/utils/subscriptionUtils'
import SubscriptionUpgradeModal from '@/components/UI/SubscriptionUpgradeModal'
import { toast } from 'react-hot-toast'
import useGoogleAnalytics from '@/hooks/useGoogleAnalytics'





const isOnline = (device) => device.lastAccess && (Date.now() - device.lastAccess < 360000)

const Schedule = () => {
  const { currentUser, currentUserData } = useAuth()
  const { getEffectiveUserId } = useWatch()
  const location = useLocation()
  const { devices, loading, deleteSchedule } = useWatchedDeviceService()
  const { screens, getScreen, getDefaultScreen, getScreenName, getScreenThumb, getScreenThumbById } = useWatchedScreenService()
  const [selectedDevice, setSelectedDevice] = useState(null)
  const [showAddSchedule, setShowAddSchedule] = useState(false)
  const [showModifySchedule, setShowModifySchedule] = useState(false)
  const [scheduleToEdit, setScheduleToEdit] = useState(null)
  const [schedules, setSchedules] = useState([])
  const [schedulesLoading, setSchedulesLoading] = useState(false)
  const [deletingScheduleId, setDeletingScheduleId] = useState(null)
  const [showDeleteSchedule, setShowDeleteSchedule] = useState(null)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  
  // Google Analytics tracking
  const { trackContentManagement, trackFeatureUsage, trackError } = useGoogleAnalytics()

  // Handle incoming navigation state to auto-open add schedule modal
  useEffect(() => {
    if (location.state?.openAddSchedule) {
      // Auto-select first device if none selected
      if (!selectedDevice && devices.length > 0) {
        setSelectedDevice(devices[0])
      }
      setShowAddSchedule(true)
      // Clear the state to prevent reopening on subsequent renders
      window.history.replaceState({}, document.title)
    }
  }, [location.state, selectedDevice, devices])

  // Handle AI agent create schedule requests
  useEffect(() => {
    if (location.state?.createScheduleForDevice) {
      const deviceKey = location.state.createScheduleForDevice
      const device = devices.find(d => d.key === deviceKey)
      if (device) {
        setSelectedDevice(device)
        setShowAddSchedule(true)
        // Clear the state to prevent reopening on subsequent renders
        window.history.replaceState({}, document.title)
      }
    }
  }, [location.state, devices])

  // Handle AI agent modify schedule requests
  useEffect(() => {
    if (location.state?.modifyScheduleForDevice) {
      const deviceKey = location.state.modifyScheduleForDevice
      const device = devices.find(d => d.key === deviceKey)
      if (device) {
        setSelectedDevice(device)
        // Clear the state to prevent reopening on subsequent renders
        window.history.replaceState({}, document.title)
      }
    }
  }, [location.state, devices])

  // Handle AI agent delete schedule requests
  useEffect(() => {
    if (location.state?.deleteScheduleForDevice) {
      const deviceKey = location.state.deleteScheduleForDevice
      const device = devices.find(d => d.key === deviceKey)
      if (device) {
        setSelectedDevice(device)
        // Clear the state to prevent reopening on subsequent renders
        window.history.replaceState({}, document.title)
      }
    }
  }, [location.state, devices])



  // Fetch schedules for selected device
  React.useEffect(() => {
    if (selectedDevice && selectedDevice.schedule) {
      setSchedules(selectedDevice.schedule || [])
    } else {
      setSchedules([])
    }
  }, [selectedDevice])

  // Format schedule time for display
  const formatScheduleTime = (timestamp) => {
    return new Date(timestamp).toLocaleString()
  }

  // Check if schedule is currently running
  const isScheduleRunning = (schedule) => {
    const now = new Date()
    const currentTime = now.getTime()
    const currentDay = now.getDay() // 0 = Sunday, 1 = Monday, etc.

    if (schedule.isRepetitive) {
      // Check if today is enabled and current time is within the time range
      const todaySchedule = schedule.days.find(day => day.index === currentDay)
      if (!todaySchedule || !todaySchedule.enabled) {
        return false
      }
      
      const currentHour = now.getHours()
      return currentHour >= todaySchedule.startHour && currentHour < todaySchedule.endHour
    } else {
      // Check if current time is within the schedule's start and end time
      return currentTime >= schedule.start && currentTime <= schedule.end
    }
  }

  // Handle schedule actions
  const handleEditSchedule = (schedule) => {
    setScheduleToEdit(schedule)
    setShowModifySchedule(true)
    
    // Track schedule edit action
    trackContentManagement('edit_schedule', 'schedule')
  }

  const handleDeleteSchedule = (schedule) => {
    setShowDeleteSchedule(schedule)
    
    // Track schedule delete action
    trackContentManagement('delete_schedule', 'schedule')
  }

  const confirmDeleteSchedule = async (schedule) => {
    setDeletingScheduleId(schedule.id)
    
    try {
      // Call DeviceService to delete the schedule
      const updatedDevice = await deleteSchedule(selectedDevice.key, schedule)
      
      // Update local state with the updated device
      setSelectedDevice(updatedDevice)
      setSchedules(updatedDevice.schedule || [])
      
      toast.success('Schedule deleted successfully')
      setShowDeleteSchedule(null)
      
      // Track successful schedule deletion
      trackContentManagement('schedule_deleted', 'schedule')
    } catch (error) {
      toast.error('Failed to delete schedule. Please try again.')
      
      // Track schedule deletion error
      trackError('schedule_deletion_error', error.message)
    } finally {
      setDeletingScheduleId(null)
    }
  }

  // Handle schedule updates
  const handleScheduleAdded = (updatedSchedules) => {
    // Update the selected device with new schedules
    setSelectedDevice(prev => ({
      ...prev,
      schedule: updatedSchedules
    }));
    // Update the schedules list
    setSchedules(updatedSchedules);
    
    // Track schedule addition
    trackContentManagement('add_schedule', 'schedule')
  };

  const handleScheduleUpdated = (updatedSchedules) => {
    // Update the selected device with updated schedules
    setSelectedDevice(prev => ({
      ...prev,
      schedule: updatedSchedules
    }));
    // Update the schedules list
    setSchedules(updatedSchedules);
    
    // Track schedule update
    trackContentManagement('update_schedule', 'schedule')
  };

  // Select first device by default
  React.useEffect(() => {
    if (!selectedDevice && devices.length > 0) {
      setSelectedDevice(devices[0])
    }
  }, [devices, selectedDevice])

  // Update running status every minute
  React.useEffect(() => {
    if (schedules.length === 0) return

    const interval = setInterval(() => {
      // Force re-render to update running status
      setSchedules(prev => [...prev])
    }, 60000) // Update every minute

    return () => clearInterval(interval)
  }, [schedules.length])

          // Filter devices based on user subscription - free users can't see channel devices
  const filteredDevices = React.useMemo(() => {
            // Filter out channel devices (limited = true)
    return devices.filter(device => !device.limited)
  }, [devices])

  return (
    <div className="flex h-[80vh] bg-gray-50 rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      {/* Device List */}
      <div className="w-[420px] bg-white border-r border-gray-200 overflow-y-auto flex-shrink-0 p-6">
        <div className="font-semibold text-gray-700 text-lg mb-4 pl-2">Devices</div>
        {loading ? (
          <div className="text-gray-400 text-center py-8">Loading...</div>
        ) : filteredDevices.length === 0 ? (
          <div className="text-gray-400 text-center py-8">No devices found</div>
        ) : (
          <div className="space-y-4">
            {filteredDevices
              .sort((a, b) => {
                // Sort by online status first (online devices first), then by name
                const aOnline = isOnline(a);
                const bOnline = isOnline(b);
                if (aOnline !== bOnline) {
                  return bOnline ? 1 : -1; // Online devices first
                }
                return (a.name || '').localeCompare(b.name || '');
              })
              .map(device => (
              <button
                key={device.key}
                className={`w-full text-left bg-gray-50 rounded-xl p-6 border border-gray-100 flex items-center justify-between hover:border-yellow-200 hover:shadow-md transition-all duration-200 ${selectedDevice?.key === device.key ? 'ring-2 ring-yellow-400 border-yellow-200' : ''}`}
                onClick={() => setSelectedDevice(device)}
              >
                {/* Left side - Device info */}
                <div className="flex items-center gap-4 flex-1">
                  {/* Status indicator */}
                  <div className="flex flex-col items-center gap-1">
                    {isOnline(device) ? (
                      <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                    ) : (
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    )}
                    <span className="text-xs text-gray-500">
                      {isOnline(device) ? 'Online' : 'Offline'}
                    </span>
                  </div>
                  {/* Device name and details */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">{device.name}</h3>
                      {device.limited && (
                        <div className="flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                          <Tv className="w-3 h-3" />
                          Channel
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span>v{device.version || '1.0.0'}</span>
                      <span>•</span>
                      <span>{device.manufacturer} {device.model}</span>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
        
        {/* Show message about channel devices being hidden */}
        {!loading && devices.length > filteredDevices.length && (
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2 text-blue-800">
              <Tv className="w-4 h-4" />
              <span className="text-sm font-medium">Channel Devices Hidden</span>
            </div>
            <p className="text-xs text-blue-700 mt-1">
              {devices.length - filteredDevices.length} channel device{devices.length - filteredDevices.length !== 1 ? 's' : ''} hidden from the schedule view.
            </p>
          </div>
        )}
      </div>
      {/* Schedule List */}
      <div className="flex-1 flex flex-col bg-white">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {selectedDevice ? `Schedules for "${selectedDevice.name || selectedDevice.id}"` : 'Select a device'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {selectedDevice ? `${schedules.length} schedule${schedules.length !== 1 ? 's' : ''} configured` : ''}
            </p>
          </div>
          {selectedDevice && (
            <button 
              className="px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2 bg-yellow-500 text-white hover:bg-yellow-600"
              onClick={() => {
                if (canAddSchedules(currentUserData)) {
                  setShowAddSchedule(true)
                  trackContentManagement('add_schedule_button_click', 'schedule')
                } else {
                  setShowUpgradeModal(true)
                  trackFeatureUsage('subscription_upgrade_required', 'add_schedule')
                }
              }}
            >
              <span>+</span>
              <span>New Schedule</span>
            </button>
          )}
        </div>

        {/* Schedule List Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!selectedDevice ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-gray-400 text-lg">Select a device to view its schedules</div>
            </div>
          ) : schedulesLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-gray-400">Loading schedules...</div>
            </div>
          ) : schedules.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="text-lg text-gray-700 mb-2">
                No schedules configured for this device
              </div>
              <p className="text-gray-500 mb-4">Create your first schedule to start displaying content on this device</p>
            </div>
          ) : (
            <div className="space-y-4">
              {schedules.map((schedule, index) => {
                const screen = getScreen(schedule.screenId)
                const screenThumb = getScreenThumbById(schedule.screenId)
                
                return (
                  <div key={schedule.id || index} className="bg-gray-50 rounded-xl p-6 border border-gray-200 hover:border-yellow-200 transition-all">
                    <div className="flex items-start gap-4">
                      {/* Screen Preview */}
                      <div className="flex-shrink-0">
                        <img
                          src={screenThumb || 'https://via.placeholder.com/120x80?text=No+Preview'}
                          alt="Screen preview"
                          className="w-30 h-20 object-cover rounded-lg border border-gray-200"
                        />
                      </div>
                      
                      {/* Schedule Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-lg font-semibold text-gray-900">
                                {screen?.name || 'Unknown Screen'}
                              </h3>
                              {isScheduleRunning(schedule) && (
                                <div className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                                  <span>LIVE</span>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-600">
                              {schedule.isRepetitive ? (
                                <div className="flex flex-col gap-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">Days:</span>
                                    <span className="flex gap-1">
                                      {schedule.days.filter(d => d.enabled).map((day, idx) => (
                                        <span key={day.index} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                                          {day.name}
                                        </span>
                                      ))}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">Time:</span>
                                    <span>
                                      {schedule.days.filter(d => d.enabled).map((day, idx) => (
                                        <span key={day.index} className="text-xs">
                                          {day.name}: {String(day.startHour).padStart(2, '0')}:00 - {String(day.endHour).padStart(2, '0')}:00
                                          {idx < schedule.days.filter(d => d.enabled).length - 1 ? ', ' : ''}
                                        </span>
                                      ))}
                                    </span>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <span className="flex items-center gap-1">
                                    <span className="font-medium">Start:</span>
                                    <span>{formatScheduleTime(schedule.start)}</span>
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <span className="font-medium">End:</span>
                                    <span>{formatScheduleTime(schedule.end)}</span>
                                  </span>
                                </>
                              )}
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                schedule.isRepetitive 
                                  ? 'bg-blue-100 text-blue-800' 
                                  : 'bg-green-100 text-green-800'
                              }`}>
                                {schedule.isRepetitive ? 'Repetitive' : 'One-time'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleEditSchedule(schedule)}
                          className="p-2 text-gray-600 hover:text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                          title="Edit Schedule"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteSchedule(schedule)}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete Schedule"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Add Schedule Modal */}
      {selectedDevice && (
        <AddScheduleModal
          open={showAddSchedule}
          onClose={() => setShowAddSchedule(false)}
          onCreate={() => setShowAddSchedule(false)}
          deviceName={selectedDevice.name || selectedDevice.id}
          screenOptions={screens.map(screen => ({
            label: screen.name,
            value: screen.key,
            thumbUrl: screen.thumbUrl || screen.imageUniversalUrl || screen.imageUrl || '',
            imageUniversalUrl: screen.imageUniversalUrl,
            imageUrl: screen.imageUrl,
            thumbUniversalUrl: screen.thumbUniversalUrl,
            slidesData: screen.slidesData,
            videoUrl: screen.videoUrl
          }))}
          defaultScreen={screens[0]?.key || ''}
          device={selectedDevice}
          onScheduleAdded={handleScheduleAdded}
        />
      )}

      {/* Modify Schedule Modal */}
      {selectedDevice && scheduleToEdit && (
        <ModifyScheduleModal
          open={showModifySchedule}
          onClose={() => {
            setShowModifySchedule(false)
            setScheduleToEdit(null)
          }}
          onUpdate={() => {
            setShowModifySchedule(false)
            setScheduleToEdit(null)
          }}
          deviceName={selectedDevice.name || selectedDevice.id}
          screenOptions={screens.map(screen => ({
            label: screen.name,
            value: screen.key,
            thumbUrl: screen.thumbUrl || screen.imageUniversalUrl || screen.imageUrl || '',
            imageUniversalUrl: screen.imageUniversalUrl,
            imageUrl: screen.imageUrl,
            thumbUniversalUrl: screen.thumbUniversalUrl,
            slidesData: screen.slidesData,
            videoUrl: screen.videoUrl
          }))}
          device={selectedDevice}
          scheduleToEdit={scheduleToEdit}
          onScheduleUpdated={handleScheduleUpdated}
        />
      )}

      {/* Subscription Upgrade Modal */}
      <SubscriptionUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="schedules"
      />

      {/* Delete Schedule Modal */}
      {showDeleteSchedule && createPortal(
        <ScheduleDeleteModal
          schedule={showDeleteSchedule}
          screen={getScreen(showDeleteSchedule.screenId)}
          screenThumb={getScreenThumbById(showDeleteSchedule.screenId)}
          onClose={() => setShowDeleteSchedule(null)}
          onDelete={confirmDeleteSchedule}
          isDeleting={deletingScheduleId === showDeleteSchedule.id}
        />,
        document.body
      )}
    </div>
  )
}

export default Schedule

// --- Modal Components ---

function ScheduleDeleteModal({ schedule, screen, screenThumb, onClose, onDelete, isDeleting }) {
  React.useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = 'unset' }
  }, [])
  
  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-60 z-[9999] flex items-center justify-center">
      <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-lg relative">
        <h2 className="text-xl font-bold mb-4">Delete Schedule</h2>
        <div className="mb-4">
          <p className="text-gray-700 mb-3">
            Are you sure you want to delete this schedule?
          </p>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-3 mb-2">
              <img
                src={screenThumb || 'https://via.placeholder.com/60x40?text=No+Preview'}
                alt="Screen preview"
                className="w-15 h-10 object-cover rounded border border-gray-200"
              />
              <div>
                <p className="font-semibold text-gray-900">{screen?.name || 'Unknown Screen'}</p>
                <p className="text-sm text-gray-600">
                  {schedule.isRepetitive ? 'Repetitive Schedule' : 'One-time Schedule'}
                </p>
              </div>
            </div>
            <div className="text-sm text-gray-600">
              <p><span className="font-medium">Start:</span> {new Date(schedule.start).toLocaleString()}</p>
              <p><span className="font-medium">End:</span> {new Date(schedule.end).toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <button 
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            onClick={onClose}
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            onClick={() => onDelete(schedule)}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Deleting...</span>
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                <span>Delete</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
} 